import React, { useState } from 'react';
import { useAppDispatch, useAppSelector } from "../../../../store/hooks";

import {Button as ButtonAnt, Image, message, Space, Switch, Tag} from "antd";
import { StickerInterface, selectAllStickers, setEditId } from "../../../../store/sticker_maker/stickersSlice";
import { ColumnsType } from "antd/es/table";
import Table from "../../../../componets/Table";
import { formattedDate } from "../../../../helpers/DateHelpers";
import {Button, ButtonDestroy, ButtonEdit} from "../../../../componets/Buttons";
import { EntityId } from "@reduxjs/toolkit";
import {
  groupApproveSticker,
  groupBlockSticker,
  approveSticker,
  blockSticker,
  destroySticker, groupDestroySticker
} from "../../../../actions/sticker_maker/stickersActions";
import Meta from '../../StickerPacks/_partials/_Meta';
import {
  CheckCircleOutlined, DeleteOutlined,
  ExclamationCircleOutlined,
  StopOutlined,
  SwitcherOutlined
} from '@ant-design/icons';

interface ListTableInterface {
  className?: string;
}

const _ListTable = ({ className }: ListTableInterface) => {
  const dispatch                              = useAppDispatch();
  const [selectedRowKeys, setSelectedRowKeys] = useState<React.Key[]>([]);
  const [reviewMode, setReviewMode]           = useState<boolean>(true);
  const stickers                              = useAppSelector(selectAllStickers);
  const stickerPacks                          = useAppSelector(state => state.stickerMaker.stickerPacks.entities);
  const hasSelected                           = selectedRowKeys.length > 0;
  const rowSelection                          = {
    selectedRowKeys,
    onChange: setSelectedRowKeys,
    getCheckboxProps: (record: StickerInterface) => ({
      disabled: reviewMode && record.status !== 0
    }),
  };

  function onClickGroupApprove() {
    handleGroupAction(groupApproveSticker).catch(console.error);
  }

  function onClickGroupBlock() {
    handleGroupAction(groupBlockSticker).catch(console.error);
  }

  function onClickGroupDestroy() {
    handleGroupDestroy().catch(console.error);
  }

  async function handleGroupAction (action: any) {
    const resultAction = await dispatch(action(selectedRowKeys));

    if (action.fulfilled.match(resultAction)) {
      setSelectedRowKeys([]);
      message.success(`Items ${selectedRowKeys.join(', ')} was successful updated`);
    }

    if (action.rejected.match(resultAction)) {
      message.error(resultAction.error.message);
    }
  }

  async function handleGroupDestroy () {
    const resultAction = await dispatch(groupDestroySticker(selectedRowKeys));

    if (groupDestroySticker.fulfilled.match(resultAction)) {
      setSelectedRowKeys([]);
      message.success(`Items ${selectedRowKeys.join(', ')} was successful deleted`);
    }

    if (groupDestroySticker.rejected.match(resultAction)) {
      message.error(resultAction.error.message);
    }
  }

  function onClickApprove(id: number) {
    dispatch(approveSticker(id));
  }

  function onClickBlock(id: number) {
    dispatch(blockSticker(id));
  }

  function onClickEdit(id: EntityId) {
    dispatch(setEditId(id));
  }

  function onClickDestroy(id: EntityId, stickerPackId: EntityId) {
    handleDestroy(id, stickerPackId).catch(console.error);
  }

  async function handleDestroy (id: any, stickerPackId: any) {
    const resultAction = await dispatch(destroySticker({ id, stickerPackId }));

    if (destroySticker.fulfilled.match(resultAction)) {
      const filtered = selectedRowKeys.filter(key => key !== id);

      setSelectedRowKeys([...filtered]);
      message.success(`ItemID ${id} was successful deleted`);
    }

    if (destroySticker.rejected.match(resultAction)) {
      message.error(resultAction.error.message);
    }
  }

  const statusLabel = (status: number) => {
    switch (status) {
      case 0:
        return <Tag color='warning' icon={<ExclamationCircleOutlined />}>in review</Tag>;
      case 1:
        return <Tag color='success' icon={<CheckCircleOutlined />}>approved</Tag>;
      case 2:
        return <Tag color='error' icon={<StopOutlined />}>blocked</Tag>;
    }

    return <Tag color='default' icon={<SwitcherOutlined />}></Tag>;
  }

  const columns: ColumnsType<StickerInterface> = [
    {
      key: 'image_url',
      title: 'Sticker',
      dataIndex: 'image_url',
      render: (_, { image_url }) => {
        return <Image src={image_url} width={44} />
      },
      width: 70
    },

    {
      key: 'sticker_pack',
      title: 'Sticker Pack',
      dataIndex: 'sticker_pack',
      render: (_, { sticker_pack }) => {
        const stickerPack = sticker_pack ? stickerPacks[sticker_pack] : undefined;
        return <Meta stickerPack={stickerPack} size='small' />;
      },
      width: 200,
      ellipsis: true
    },

    {
      key: 'status',
      title: 'Status',
      render: (_, { status }) => {
        return statusLabel(status);
      },
      width: 125
    },

    {
      key: 'created_at',
      title: 'Created at',
      render: (_, { created_at }) => {
        return <small>{formattedDate(created_at).dateTime}</small>;
      },
      width: 125
    },

    {
      key: 'updated',
      title: 'Updated at',
      render: (_, { updated_at }) => {
        return <small>{formattedDate(updated_at).dateTime}</small>;
      },
      width: 125
    },

    {
      key: 'action',
      title: 'Action',
      render: (_, { id, sticker_pack_id}) => {
        return (
          <Space size="middle">
            <Button title='Approve' onClick={() => onClickApprove(id)} icon={CheckCircleOutlined} />
            <Button title='Block' onClick={() => onClickBlock(id)} icon={StopOutlined} />
            <ButtonEdit key={'button-edit'} onClick={() => onClickEdit(id)} />
            <ButtonDestroy key='button-destroy' onClick={() => onClickDestroy(id, sticker_pack_id)} />
          </Space>
        );
      },
      width: 250
    },
  ];

  return (
    <div className={className}>
      <div className='v-mb-10'>
        <ButtonAnt
          type="primary"
          onClick={onClickGroupApprove}
          disabled={!hasSelected}
          className='v-mr-10'
          size='small'
          icon={<CheckCircleOutlined />}
        >
          Approve
        </ButtonAnt>
        <ButtonAnt
          danger
          type="primary"
          onClick={onClickGroupBlock}
          disabled={!hasSelected}
          className='v-mr-10'
          size='small'
          icon={<StopOutlined />}
        >
          Block
        </ButtonAnt>
        <ButtonAnt
          danger
          type="primary"
          onClick={onClickGroupDestroy}
          disabled={!hasSelected}
          className='v-mr-10'
          size='small'
          icon={<DeleteOutlined />}
        >
          Destroy
        </ButtonAnt>

        <Switch
          checkedChildren="Review On"
          unCheckedChildren="Review Off"
          checked={reviewMode}
          onChange={() => {
            setReviewMode(!reviewMode);
          }}
        />

        <span style={{ marginLeft: 8 }}>
          {hasSelected ? `Selected ${selectedRowKeys.length} stickers` : ''}
        </span>
      </div>

      <Image.PreviewGroup>
        <Table
          rowKey='id'
          rowSelection={rowSelection}
          columns={columns}
          dataSource={stickers}
        />
      </Image.PreviewGroup>
    </div>
  );
}

export default _ListTable;
